import { DXFData, DXFEntity } from '../new-project/dxf/types';

/** Target size: normalize SVG so longest dimension = this many meters */
const TARGET_SIZE_METERS = 100;

/** Skip entities whose area is below this fraction of the total bounding area */
const MIN_AREA_FRACTION = 0.001;

/**
 * Parse an SVG string and extract layers (top-level <g> groups)
 * and their paths as entities compatible with DXFData.
 *
 * - Coordinates are normalized to meters (longest dimension → 100m).
 * - Only closed entities are kept.
 * - Tiny entities (< 0.1% of bounding area) are skipped.
 * - Adjacent small entities of the same layer are merged.
 */
export function parseSVG(svgText: string): DXFData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) return { layers: [], entities: [] };

  // Get SVG viewBox or width/height for coordinate space
  const vb = svg.getAttribute('viewBox');
  let svgWidth = 0;
  let svgHeight = 0;
  if (vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    svgWidth = parts[2] ?? 0;
    svgHeight = parts[3] ?? 0;
  } else {
    svgWidth = parseFloat(svg.getAttribute('width') ?? '0');
    svgHeight = parseFloat(svg.getAttribute('height') ?? '0');
  }

  const layers = new Set<string>();
  const rawEntities: DXFEntity[] = [];
  let layerIndex = 0;

  // Process top-level <g> groups as layers
  const groups = svg.querySelectorAll(':scope > g');

  if (groups.length > 0) {
    groups.forEach((g) => {
      const fill = g.getAttribute('fill') ?? '';
      const id = g.getAttribute('id') ?? '';
      const label = g.getAttribute('inkscape:label') ?? '';
      const layerName = label || id || `Layer ${++layerIndex} (${fill})`;
      layers.add(layerName);
      extractEntitiesFromElement(g, layerName, svgHeight, rawEntities);
    });
  } else {
    const layerName = 'Default';
    layers.add(layerName);
    extractEntitiesFromElement(svg, layerName, svgHeight, rawEntities);
  }

  // ── Post-processing ────────────────────────────────────────────────────────

  // 1. Keep only closed entities with >= 3 vertices
  let entities = rawEntities.filter((e) => e.closed && e.vertices.length >= 3);

  // 2. Normalize coordinates: scale so longest SVG dimension → TARGET_SIZE_METERS
  const longestDim = Math.max(svgWidth, svgHeight, 1);
  const scale = TARGET_SIZE_METERS / longestDim;
  if (Math.abs(scale - 1) > 0.001) {
    for (const e of entities) {
      e.vertices = e.vertices.map(([x, y]) => [x * scale, y * scale] as [number, number]);
      e.center = [e.center[0] * scale, e.center[1] * scale];
      e.radius *= scale;
    }
  }

  // 3. Compute bounding area and filter out tiny entities
  const totalArea = svgWidth * scale * (svgHeight * scale);
  const minArea = totalArea * MIN_AREA_FRACTION;
  entities = entities.filter((e) => Math.abs(polyAreaSigned(e.vertices)) >= minArea);

  // 4. Merge small entities within the same layer
  entities = mergeSmallEntities(entities, totalArea);

  // Only keep layers that still have entities
  const usedLayers = new Set(entities.map((e) => e.layer));
  const finalLayers = [...layers].filter((l) => usedLayers.has(l)).sort();

  return { layers: finalLayers, entities };
}

// ── Entity extraction ─────────────────────────────────────────────────────────

function extractEntitiesFromElement(parent: Element, layerName: string, svgHeight: number, entities: DXFEntity[]) {
  // <path> elements
  parent.querySelectorAll('path').forEach((pathEl) => {
    const d = pathEl.getAttribute('d');
    if (!d) return;

    const subPaths = splitSubPaths(d);
    for (const subPath of subPaths) {
      const vertices = parseSVGPath(subPath, svgHeight);
      if (vertices.length < 3) continue;

      const closed = isPathClosed(subPath, vertices);
      if (!closed) continue; // skip unclosed paths
      entities.push(makeEntity('PATH', layerName, vertices, true));
    }
  });

  // <polygon> elements
  parent.querySelectorAll('polygon').forEach((el) => {
    const pts = parsePointsAttr(el.getAttribute('points') ?? '', svgHeight);
    if (pts.length >= 3) {
      entities.push(makeEntity('POLYGON', layerName, pts, true));
    }
  });

  // <rect> elements
  parent.querySelectorAll('rect').forEach((el) => {
    const x = parseFloat(el.getAttribute('x') ?? '0');
    const y = parseFloat(el.getAttribute('y') ?? '0');
    const w = parseFloat(el.getAttribute('width') ?? '0');
    const h = parseFloat(el.getAttribute('height') ?? '0');
    if (w > 0 && h > 0) {
      const vertices: [number, number][] = [
        [x, svgHeight - y],
        [x + w, svgHeight - y],
        [x + w, svgHeight - (y + h)],
        [x, svgHeight - (y + h)],
      ];
      entities.push(makeEntity('RECT', layerName, vertices, true));
    }
  });

  // <circle> elements
  parent.querySelectorAll('circle').forEach((el) => {
    const cx = parseFloat(el.getAttribute('cx') ?? '0');
    const cy = parseFloat(el.getAttribute('cy') ?? '0');
    const r = parseFloat(el.getAttribute('r') ?? '0');
    if (r > 0) {
      const vertices = circleToVertices(cx, svgHeight - cy, r, 32);
      entities.push(makeEntity('CIRCLE', layerName, vertices, true));
    }
  });
}

function makeEntity(type: string, layer: string, vertices: [number, number][], closed: boolean): DXFEntity {
  return {
    type,
    layer,
    vertices,
    closed,
    radius: 0,
    startAngle: 0,
    endAngle: 360,
    center: [0, 0],
  };
}

function circleToVertices(cx: number, cy: number, r: number, segments: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  return pts;
}

function parsePointsAttr(pointsStr: string, svgHeight: number): [number, number][] {
  const nums = pointsStr
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const pts: [number, number][] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    pts.push([nums[i], svgHeight - nums[i + 1]]);
  }
  return pts;
}

// ── Merging ───────────────────────────────────────────────────────────────────

/** Signed polygon area (shoelace). Positive = CCW. */
function polyAreaSigned(pts: [number, number][]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j][0] + pts[i][0]) * (pts[j][1] - pts[i][1]);
  }
  return a / 2;
}

function entityBounds(e: DXFEntity): [number, number, number, number] {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const [x, y] of e.vertices) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

function boundsOverlap(
  a: [number, number, number, number],
  b: [number, number, number, number],
  margin: number,
): boolean {
  return a[0] - margin <= b[2] && a[2] + margin >= b[0] && a[1] - margin <= b[3] && a[3] + margin >= b[1];
}

/**
 * Merge small entities (< 5% of total area) within the same layer if their
 * bounding boxes overlap or are very close.
 */
function mergeSmallEntities(entities: DXFEntity[], totalArea: number): DXFEntity[] {
  const smallThreshold = totalArea * 0.05;
  const result: DXFEntity[] = [];
  const byLayer: Record<string, DXFEntity[]> = {};

  for (const e of entities) {
    const area = Math.abs(polyAreaSigned(e.vertices));
    if (area >= smallThreshold) {
      result.push(e);
    } else {
      (byLayer[e.layer] ??= []).push(e);
    }
  }

  // For each layer, merge small overlapping entities
  for (const [layer, smalls] of Object.entries(byLayer)) {
    if (smalls.length <= 1) {
      result.push(...smalls);
      continue;
    }

    // Greedy merge: union-find based on bbox overlap
    const parent = smalls.map((_, i) => i);
    const bounds = smalls.map(entityBounds);
    // Merge margin: entities within 2% of total dimension are considered adjacent
    const margin = Math.sqrt(totalArea) * 0.02;

    const find = (x: number): number => {
      while (parent[x] !== x) {
        parent[x] = parent[parent[x]];
        x = parent[x];
      }
      return x;
    };
    const union = (a: number, b: number) => {
      parent[find(a)] = find(b);
    };

    for (let i = 0; i < smalls.length; i++) {
      for (let j = i + 1; j < smalls.length; j++) {
        if (boundsOverlap(bounds[i], bounds[j], margin)) {
          union(i, j);
        }
      }
    }

    // Group by root
    const groups: Record<number, DXFEntity[]> = {};
    for (let i = 0; i < smalls.length; i++) {
      const root = find(i);
      (groups[root] ??= []).push(smalls[i]);
    }

    for (const group of Object.values(groups)) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        // Merge into one entity: pick the largest and append other vertices
        group.sort((a, b) => Math.abs(polyAreaSigned(b.vertices)) - Math.abs(polyAreaSigned(a.vertices)));
        const merged = { ...group[0], vertices: [...group[0].vertices] };
        for (let k = 1; k < group.length; k++) {
          merged.vertices.push(...group[k].vertices);
        }
        merged.layer = layer;
        result.push(merged);
      }
    }
  }

  return result;
}

// ── SVG Path Parsing ─────────────────────────────────────────────────────────

function splitSubPaths(d: string): string[] {
  const result: string[] = [];
  const parts = d.split(/(?=[Mm])/);
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed) result.push(trimmed);
  }
  return result;
}

function isPathClosed(d: string, vertices: [number, number][]): boolean {
  if (/[Zz]/.test(d)) return true;
  if (vertices.length >= 3) {
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    const dist = Math.hypot(first[0] - last[0], first[1] - last[1]);
    if (dist < 0.5) return true;
  }
  return false;
}

function tokenizePath(d: string): (string | number)[] {
  const tokens: (string | number)[] = [];
  const re = /([a-zA-Z])|([+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    if (m[1]) tokens.push(m[1]);
    else if (m[2]) tokens.push(parseFloat(m[2]));
  }
  return tokens;
}

function parseSVGPath(d: string, svgHeight: number): [number, number][] {
  const tokens = tokenizePath(d);
  const pts: [number, number][] = [];
  let cx = 0,
    cy = 0;
  let sx = 0,
    sy = 0;
  let prevCx2 = 0,
    prevCy2 = 0;
  let prevCmd = '';
  let i = 0;

  function next(): number {
    return tokens[i++] as number;
  }

  function flip(y: number): number {
    return svgHeight - y;
  }

  function addPt(x: number, y: number) {
    cx = x;
    cy = y;
    pts.push([x, flip(y)]);
  }

  while (i < tokens.length) {
    let cmd = tokens[i];
    if (typeof cmd === 'string') {
      i++;
    } else {
      cmd = prevCmd;
    }

    switch (cmd) {
      case 'M':
        cx = next();
        cy = next();
        sx = cx;
        sy = cy;
        addPt(cx, cy);
        prevCmd = 'L';
        break;
      case 'm':
        cx += next();
        cy += next();
        sx = cx;
        sy = cy;
        addPt(cx, cy);
        prevCmd = 'l';
        break;
      case 'L':
        addPt(next(), next());
        prevCmd = 'L';
        break;
      case 'l':
        addPt(cx + next(), cy + next());
        prevCmd = 'l';
        break;
      case 'H':
        addPt(next(), cy);
        prevCmd = 'H';
        break;
      case 'h':
        addPt(cx + next(), cy);
        prevCmd = 'h';
        break;
      case 'V':
        addPt(cx, next());
        prevCmd = 'V';
        break;
      case 'v':
        addPt(cx, cy + next());
        prevCmd = 'v';
        break;
      case 'C': {
        const x1 = next(),
          y1 = next();
        const x2 = next(),
          y2 = next();
        const x = next(),
          y = next();
        cubicBezier(cx, cy, x1, y1, x2, y2, x, y, svgHeight, pts);
        prevCx2 = x2;
        prevCy2 = y2;
        cx = x;
        cy = y;
        prevCmd = 'C';
        break;
      }
      case 'c': {
        const dx1 = next(),
          dy1 = next();
        const dx2 = next(),
          dy2 = next();
        const dx = next(),
          dy = next();
        const x1 = cx + dx1,
          y1 = cy + dy1;
        const x2 = cx + dx2,
          y2 = cy + dy2;
        const x = cx + dx,
          y = cy + dy;
        cubicBezier(cx, cy, x1, y1, x2, y2, x, y, svgHeight, pts);
        prevCx2 = x2;
        prevCy2 = y2;
        cx = x;
        cy = y;
        prevCmd = 'c';
        break;
      }
      case 'S': {
        const rx1 = 2 * cx - prevCx2,
          ry1 = 2 * cy - prevCy2;
        const x2 = next(),
          y2 = next();
        const x = next(),
          y = next();
        cubicBezier(cx, cy, rx1, ry1, x2, y2, x, y, svgHeight, pts);
        prevCx2 = x2;
        prevCy2 = y2;
        cx = x;
        cy = y;
        prevCmd = 'S';
        break;
      }
      case 's': {
        const rx1 = 2 * cx - prevCx2,
          ry1 = 2 * cy - prevCy2;
        const dx2 = next(),
          dy2 = next();
        const dx = next(),
          dy = next();
        const x2 = cx + dx2,
          y2 = cy + dy2;
        const x = cx + dx,
          y = cy + dy;
        cubicBezier(cx, cy, rx1, ry1, x2, y2, x, y, svgHeight, pts);
        prevCx2 = x2;
        prevCy2 = y2;
        cx = x;
        cy = y;
        prevCmd = 's';
        break;
      }
      case 'Q': {
        const x1 = next(),
          y1 = next();
        const x = next(),
          y = next();
        quadBezier(cx, cy, x1, y1, x, y, svgHeight, pts);
        prevCx2 = x1;
        prevCy2 = y1;
        cx = x;
        cy = y;
        prevCmd = 'Q';
        break;
      }
      case 'q': {
        const dx1 = next(),
          dy1 = next();
        const dx = next(),
          dy = next();
        const x1 = cx + dx1,
          y1 = cy + dy1;
        const x = cx + dx,
          y = cy + dy;
        quadBezier(cx, cy, x1, y1, x, y, svgHeight, pts);
        prevCx2 = x1;
        prevCy2 = y1;
        cx = x;
        cy = y;
        prevCmd = 'q';
        break;
      }
      case 'T': {
        const rx1 = 2 * cx - prevCx2,
          ry1 = 2 * cy - prevCy2;
        const x = next(),
          y = next();
        quadBezier(cx, cy, rx1, ry1, x, y, svgHeight, pts);
        prevCx2 = rx1;
        prevCy2 = ry1;
        cx = x;
        cy = y;
        prevCmd = 'T';
        break;
      }
      case 't': {
        const rx1 = 2 * cx - prevCx2,
          ry1 = 2 * cy - prevCy2;
        const x = cx + next(),
          y = cy + next();
        quadBezier(cx, cy, rx1, ry1, x, y, svgHeight, pts);
        prevCx2 = rx1;
        prevCy2 = ry1;
        cx = x;
        cy = y;
        prevCmd = 't';
        break;
      }
      case 'A':
      case 'a': {
        next(); // rx
        next(); // ry
        next(); // x-rotation
        next(); // large-arc-flag
        next(); // sweep-flag
        const x = cmd === 'A' ? next() : cx + next();
        const y = cmd === 'A' ? next() : cy + next();
        addPt(x, y);
        prevCmd = cmd;
        break;
      }
      case 'Z':
      case 'z':
        cx = sx;
        cy = sy;
        prevCmd = 'Z';
        break;
      default:
        i++;
        break;
    }
  }

  return pts;
}

function cubicBezier(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  svgHeight: number,
  pts: [number, number][],
  segments = 8,
) {
  for (let s = 1; s <= segments; s++) {
    const t = s / segments;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const x = mt3 * x0 + 3 * mt2 * t * x1 + 3 * mt * t2 * x2 + t3 * x3;
    const y = mt3 * y0 + 3 * mt2 * t * y1 + 3 * mt * t2 * y2 + t3 * y3;
    pts.push([x, svgHeight - y]);
  }
}

function quadBezier(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  svgHeight: number,
  pts: [number, number][],
  segments = 6,
) {
  for (let s = 1; s <= segments; s++) {
    const t = s / segments;
    const mt = 1 - t;
    const x = mt * mt * x0 + 2 * mt * t * x1 + t * t * x2;
    const y = mt * mt * y0 + 2 * mt * t * y1 + t * t * y2;
    pts.push([x, svgHeight - y]);
  }
}

/**
 * Simplify a polygon using the Ramer-Douglas-Peucker algorithm.
 */
export function simplifyPolygon(points: [number, number][], epsilon: number): [number, number][] {
  if (points.length <= 2) return points;

  let maxDist = 0;
  let maxIdx = 0;
  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const d = pointToLineDist(points[i], first, last);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = simplifyPolygon(points.slice(0, maxIdx + 1), epsilon);
    const right = simplifyPolygon(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [first, last];
}

function pointToLineDist(p: [number, number], a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}
